import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";
import { readJsonStorage, writeJsonStorage } from "../../../lib/storage-utils";
import { defaultBusinessOrder } from "./merchantNavigation";

const STORAGE_KEY = "customy.merchant-shell.v2";

export interface MerchantShellState {
  topVisible: boolean;
  toolsVisible: boolean;
  focusMode: boolean;
  arranging: boolean;
  businessOrder: string[];
}

type MerchantShellAction =
  | { type: "toggle-top" }
  | { type: "toggle-tools" }
  | { type: "toggle-focus" }
  | { type: "set-arranging"; value: boolean }
  | { type: "set-business-order"; value: string[] }
  | { type: "reset-business-order" };

const defaultState: MerchantShellState = {
  topVisible: true,
  toolsVisible: true,
  focusMode: false,
  arranging: false,
  businessOrder: defaultBusinessOrder,
};

export function normalizeBusinessOrder(value: unknown): string[] {
  if (!Array.isArray(value)) return [...defaultBusinessOrder];
  const allowed = new Set(defaultBusinessOrder);
  const valid = value.filter((id): id is string => typeof id === "string" && allowed.has(id));
  return [...new Set(valid), ...defaultBusinessOrder.filter((id) => !valid.includes(id))];
}

function getInitialState(): MerchantShellState {
  const stored = (readJsonStorage(STORAGE_KEY, null) || {}) as Partial<MerchantShellState>;
  const topVisible = stored.topVisible !== false;
  const toolsVisible = stored.toolsVisible !== false;
  return {
    ...defaultState,
    topVisible,
    toolsVisible,
    focusMode: !topVisible && !toolsVisible,
    businessOrder: normalizeBusinessOrder(stored.businessOrder),
  };
}

export function merchantShellReducer(
  state: MerchantShellState,
  action: MerchantShellAction,
): MerchantShellState {
  switch (action.type) {
    case "toggle-top": {
      const topVisible = !state.topVisible;
      if (!topVisible && !state.toolsVisible) {
        return {
          ...state,
          focusMode: true,
          arranging: false,
          topVisible: false,
          toolsVisible: false,
        };
      }
      return {
        ...state,
        focusMode: false,
        topVisible,
      };
    }
    case "toggle-tools": {
      const toolsVisible = !state.toolsVisible;
      if (!state.topVisible && !toolsVisible) {
        return {
          ...state,
          focusMode: true,
          arranging: false,
          topVisible: false,
          toolsVisible: false,
        };
      }
      return {
        ...state,
        focusMode: false,
        toolsVisible,
      };
    }
    case "toggle-focus":
      if (state.focusMode) {
        return {
          ...state,
          focusMode: false,
          topVisible: true,
          toolsVisible: true,
        };
      }
      return {
        ...state,
        focusMode: true,
        arranging: false,
        topVisible: false,
        toolsVisible: false,
      };
    case "set-arranging":
      return { ...state, arranging: action.value, focusMode: false, topVisible: true };
    case "set-business-order":
      return { ...state, businessOrder: normalizeBusinessOrder(action.value) };
    case "reset-business-order":
      return { ...state, businessOrder: [...defaultBusinessOrder] };
    default:
      return state;
  }
}

interface MerchantShellContextValue extends MerchantShellState {
  toggleTop: () => void;
  toggleTools: () => void;
  toggleFocus: () => void;
  setArranging: (value: boolean) => void;
  setBusinessOrder: (value: string[]) => void;
  resetBusinessOrder: () => void;
}

const MerchantShellContext = createContext<MerchantShellContextValue | null>(null);

export function MerchantShellProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(merchantShellReducer, undefined, getInitialState);

  const persist = useCallback((next: MerchantShellState) => {
    writeJsonStorage(STORAGE_KEY, {
      topVisible: next.topVisible,
      toolsVisible: next.toolsVisible,
      businessOrder: next.businessOrder,
    });
  }, []);

  useEffect(() => {
    persist(state);
  }, [persist, state]);

  const value = useMemo<MerchantShellContextValue>(
    () => ({
      ...state,
      toggleTop: () => dispatch({ type: "toggle-top" }),
      toggleTools: () => dispatch({ type: "toggle-tools" }),
      toggleFocus: () => dispatch({ type: "toggle-focus" }),
      setArranging: (next) => dispatch({ type: "set-arranging", value: next }),
      setBusinessOrder: (next) => dispatch({ type: "set-business-order", value: next }),
      resetBusinessOrder: () => dispatch({ type: "reset-business-order" }),
    }),
    [state],
  );

  return <MerchantShellContext.Provider value={value}>{children}</MerchantShellContext.Provider>;
}

export function useMerchantShell() {
  const context = useContext(MerchantShellContext);
  if (!context) {
    throw new Error("useMerchantShell must be used within MerchantShellProvider");
  }
  return context;
}
